import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Settings,
  Shield,
  Users,
  FolderKanban,
  Layers,
  Database,
  Server,
  Search,
  Loader2,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
} from 'lucide-react';
import clsx from 'clsx';
import { settingsApi, type SettingDefinitionDto, type SettingCategoryDto } from '../api';
import { SettingItem } from '../components/settings/SettingItem';

// Map category icon names to Lucide components
const CATEGORY_ICONS: Record<string, React.ElementType> = {
  settings: Settings,
  shield: Shield,
  users: Users,
  folder: FolderKanban,
  layers: Layers,
  database: Database,
  server: Server,
};

export function SettingsPage() {
  const queryClient = useQueryClient();

  // ── State ─────────────────────────────────────────────────
  const [localValues, setLocalValues] = useState<Record<string, unknown>>({});
  const [activeCategory, setActiveCategory] = useState<string>('general');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // ── Data fetching ─────────────────────────────────────────
  const { data, isLoading, error } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await settingsApi.getAll();
      return res.data.data;
    },
  });

  // Initialize local values from server
  useEffect(() => {
    if (data?.definitions) {
      const values: Record<string, unknown> = {};
      for (const def of data.definitions) {
        values[def.key] = def.currentValue;
      }
      setLocalValues(values);
    }
  }, [data]);

  // ── Mutations ─────────────────────────────────────────────
  const updateMutation = useMutation({
    mutationFn: (settings: { key: string; value: unknown }[]) =>
      settingsApi.update(settings),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setSuccessMessage(res.data.data.message);
      setTimeout(() => setSuccessMessage(''), 3000);
    },
  });

  // ── Derived data ──────────────────────────────────────────
  const definitions = data?.definitions ?? [];
  const categories = data?.categories ?? [];

  const hasUnsavedChanges = useMemo(() => {
    if (!data) return false;
    return data.definitions.some(
      def => localValues[def.key] !== undefined && String(localValues[def.key]) !== String(def.currentValue)
    );
  }, [data, localValues]);

  // Dirty keys for the current category only
  const dirtyKeysForCategory = useCallback((catId: string): string[] => {
    if (!data) return [];
    return data.definitions
      .filter(def => def.category === catId)
      .filter(def => localValues[def.key] !== undefined && String(localValues[def.key]) !== String(def.currentValue))
      .map(def => def.key);
  }, [data, localValues]);

  // Search filtering
  const searchLower = searchQuery.toLowerCase().trim();
  const filteredDefinitions = useMemo(() => {
    let defs = definitions;

    // Filter advanced
    if (!showAdvanced) {
      defs = defs.filter(d => !d.advanced);
    }

    // Filter by search
    if (searchLower) {
      defs = defs.filter(d =>
        d.label.toLowerCase().includes(searchLower) ||
        d.key.toLowerCase().includes(searchLower) ||
        d.description.toLowerCase().includes(searchLower)
      );
    }

    // Filter by dependency visibility
    defs = defs.filter(d => {
      if (!d.dependsOn) return true;
      const depValue = localValues[d.dependsOn.key];
      return depValue === d.dependsOn.value;
    });

    return defs;
  }, [definitions, showAdvanced, searchLower, localValues]);

  // Group by category
  const groupedDefinitions = useMemo(() => {
    const map = new Map<string, SettingDefinitionDto[]>();
    for (const def of filteredDefinitions) {
      const existing = map.get(def.category) ?? [];
      existing.push(def);
      map.set(def.category, existing);
    }
    return map;
  }, [filteredDefinitions]);

  // Categories with results count (for search)
  const categoriesWithCount = useMemo(() => {
    return categories
      .sort((a, b) => a.order - b.order)
      .map(cat => ({
        ...cat,
        count: groupedDefinitions.get(cat.id)?.length ?? 0,
        hasDirty: dirtyKeysForCategory(cat.id).length > 0,
      }));
  }, [categories, groupedDefinitions, dirtyKeysForCategory]);

  // ── Handlers ──────────────────────────────────────────────
  const handleValueChange = useCallback((key: string, value: unknown) => {
    setLocalValues(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleReset = useCallback((key: string) => {
    const def = definitions.find(d => d.key === key);
    if (def) {
      setLocalValues(prev => ({ ...prev, [key]: def.defaultValue }));
    }
  }, [definitions]);

  const handleSave = () => {
    if (!data) return;

    // Collect all changed settings across ALL categories
    const changes: { key: string; value: unknown }[] = [];
    for (const def of data.definitions) {
      if (localValues[def.key] !== undefined && String(localValues[def.key]) !== String(def.currentValue)) {
        changes.push({ key: def.key, value: localValues[def.key] });
      }
    }

    if (changes.length > 0) {
      updateMutation.mutate(changes);
    }
  };

  const handleCancel = () => {
    if (!data) return;
    const values: Record<string, unknown> = {};
    for (const def of data.definitions) {
      values[def.key] = def.currentValue;
    }
    setLocalValues(values);
  };

  const handleCategoryClick = (catId: string) => {
    setActiveCategory(catId);
    if (!searchLower) {
      categoryRefs.current[catId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Warn on unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // ── Render ────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Errore nel caricamento</h2>
        <p className="text-gray-500">Impossibile caricare le impostazioni.</p>
      </div>
    );
  }

  const isSearching = searchLower.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Impostazioni</h1>
        <p className="text-gray-600 mt-1">Configurazione del sistema Config Builder</p>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Cerca impostazione per nome, chiave o descrizione..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="input pl-10 w-full"
        />
      </div>

      {/* Success toast */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-[60] animate-fade-in">
          <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <p className="font-medium">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error banner */}
      {updateMutation.error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Errore nel salvataggio</p>
            <p className="text-sm text-red-600">
              {(updateMutation.error as any)?.response?.data?.error?.message ?? 'Errore sconosciuto'}
            </p>
          </div>
        </div>
      )}

      {/* Main layout: sidebar + content */}
      <div className="flex gap-6">
        {/* Sidebar */}
        <nav className="hidden md:block w-52 flex-shrink-0">
          <div className="sticky top-8 space-y-1">
            {categoriesWithCount.map(cat => {
              const Icon = CATEGORY_ICONS[cat.icon] ?? Settings;
              const isActive = !isSearching && activeCategory === cat.id;
              const hasResults = cat.count > 0;

              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryClick(cat.id)}
                  disabled={isSearching && !hasResults}
                  className={clsx(
                    'flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm font-medium transition-colors text-left',
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : hasResults || !isSearching
                        ? 'text-gray-700 hover:bg-gray-100'
                        : 'text-gray-300 cursor-not-allowed'
                  )}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1 truncate">{cat.label}</span>
                  {cat.hasDirty && (
                    <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" />
                  )}
                  {isSearching && hasResults && (
                    <span className="text-xs text-gray-400">{cat.count}</span>
                  )}
                </button>
              );
            })}

            {/* Advanced toggle */}
            <div className="pt-3 mt-3 border-t border-gray-200">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-gray-500 hover:bg-gray-100 transition-colors"
              >
                {showAdvanced ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showAdvanced ? 'Nascondi avanzate' : 'Mostra avanzate'}
              </button>
            </div>
          </div>
        </nav>

        {/* Content area */}
        <div className="flex-1 min-w-0">
          {/* Mobile category selector */}
          <div className="md:hidden mb-4">
            <select
              value={activeCategory}
              onChange={e => setActiveCategory(e.target.value)}
              className="input w-full"
            >
              {categoriesWithCount.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.label} {cat.hasDirty ? '●' : ''}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 mt-2 text-sm text-gray-500">
              <input
                type="checkbox"
                checked={showAdvanced}
                onChange={e => setShowAdvanced(e.target.checked)}
                className="rounded border-gray-300"
              />
              Mostra avanzate
            </label>
          </div>

          {isSearching ? (
            // Search results: show all matching, grouped by category
            <SearchResults
              categories={categoriesWithCount}
              groupedDefinitions={groupedDefinitions}
              localValues={localValues}
              searchQuery={searchLower}
              onValueChange={handleValueChange}
              onReset={handleReset}
              categoryRefs={categoryRefs}
            />
          ) : (
            // Normal view: show active category only
            <CategoryView
              categories={categoriesWithCount}
              groupedDefinitions={groupedDefinitions}
              localValues={localValues}
              activeCategory={activeCategory}
              onValueChange={handleValueChange}
              onReset={handleReset}
              categoryRefs={categoryRefs}
            />
          )}

          {/* Action buttons */}
          {filteredDefinitions.length > 0 && (
            <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
              {hasUnsavedChanges && (
                <span className="text-sm text-amber-600 mr-auto">
                  Hai modifiche non salvate
                </span>
              )}
              <button
                type="button"
                onClick={handleCancel}
                disabled={!hasUnsavedChanges || updateMutation.isPending}
                className="btn btn-secondary"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!hasUnsavedChanges || updateMutation.isPending}
                className="btn btn-primary"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Salva modifiche
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────

interface CategoryViewProps {
  categories: (SettingCategoryDto & { count: number; hasDirty: boolean })[];
  groupedDefinitions: Map<string, SettingDefinitionDto[]>;
  localValues: Record<string, unknown>;
  activeCategory: string;
  onValueChange: (key: string, value: unknown) => void;
  onReset: (key: string) => void;
  categoryRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
}

function CategoryView({
  categories,
  groupedDefinitions,
  localValues,
  activeCategory,
  onValueChange,
  onReset,
  categoryRefs,
}: CategoryViewProps) {
  const cat = categories.find(c => c.id === activeCategory);
  const defs = groupedDefinitions.get(activeCategory) ?? [];

  if (!cat || defs.length === 0) {
    return (
      <div className="card p-8 text-center">
        <Settings className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">Nessuna impostazione in questa categoria.</p>
      </div>
    );
  }

  return (
    <div
      ref={el => { categoryRefs.current[activeCategory] = el; }}
      className="card p-6"
    >
      <h2 className="text-lg font-semibold text-gray-900 mb-4">{cat.label}</h2>
      <div>
        {defs.map(def => (
          <SettingItem
            key={def.key}
            definition={def}
            value={localValues[def.key] ?? def.currentValue}
            onChange={onValueChange}
            onReset={onReset}
            isModified={String(localValues[def.key]) !== String(def.defaultValue)}
          />
        ))}
      </div>
    </div>
  );
}

interface SearchResultsProps {
  categories: (SettingCategoryDto & { count: number; hasDirty: boolean })[];
  groupedDefinitions: Map<string, SettingDefinitionDto[]>;
  localValues: Record<string, unknown>;
  searchQuery: string;
  onValueChange: (key: string, value: unknown) => void;
  onReset: (key: string) => void;
  categoryRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
}

function SearchResults({
  categories,
  groupedDefinitions,
  localValues,
  searchQuery,
  onValueChange,
  onReset,
  categoryRefs,
}: SearchResultsProps) {
  const categoriesWithResults = categories.filter(c => (groupedDefinitions.get(c.id)?.length ?? 0) > 0);

  if (categoriesWithResults.length === 0) {
    return (
      <div className="card p-8 text-center">
        <Search className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">
          Nessuna impostazione trovata per &ldquo;{searchQuery}&rdquo;
        </p>
      </div>
    );
  }

  const totalResults = categoriesWithResults.reduce((sum, c) => sum + (groupedDefinitions.get(c.id)?.length ?? 0), 0);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        {totalResults} risultat{totalResults === 1 ? 'o' : 'i'} per &ldquo;{searchQuery}&rdquo;
      </p>

      {categoriesWithResults.map(cat => {
        const defs = groupedDefinitions.get(cat.id) ?? [];
        return (
          <div
            key={cat.id}
            ref={el => { categoryRefs.current[cat.id] = el; }}
            className="card p-6"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{cat.label}</h2>
            <div>
              {defs.map(def => (
                <SettingItem
                  key={def.key}
                  definition={def}
                  value={localValues[def.key] ?? def.currentValue}
                  onChange={onValueChange}
                  onReset={onReset}
                  isModified={String(localValues[def.key]) !== String(def.defaultValue)}
                  highlight
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
